import jsonExport from "jsonexport/dist";
import { downloadCSV } from "react-admin";
import { formatDate, formatTimestamp } from "../../../lib/dateFormat";

function transformCarrierData(gaRequest, carrierData, carriers, agencies, users) {
  const {
    id,
    payee,
    notifyAfterApproval,
    agentSnapshot,
    agency: agencyId,
    user: userId,
    notes,
    status,
    approval,
    emailRecipients,
    createdAt,
    updatedAt,
    ga,
    unknownAgent,
    approvalChangedBy: approvalChangedById,
    approvalChangedAt,
    specialist: specialistId,
  } = gaRequest;

  const {
    npn,
    firstName,
    lastName,
    preferredName,
    phone,
    email,
    addressStreet,
    addressCity,
    addressState,
    addressZip,
    upline,
    topOfHierarchy,
    advisor,
    commissionEmail,
    enrollmentEmail,
  } = agentSnapshot;

  const {
    carrier: carrierId,
    newPolicyUnit,
    newPolicyAmount,
    renewalUnit,
    renewalAmount,
    startDate,
    hasLOAAgents,
    level,
    processor: processorId,
    status: carrierStatus,
  } = carrierData;

  const carrier = carriers[carrierId];
  const carrierName = carrier?.value || carrierId;
  const agency = agencies[agencyId];
  const user = users[userId];
  const processor = users[processorId];
  const specialist = users[specialistId];
  const approvalChangedBy = users[approvalChangedById];

  const processorName = (processor) ? `${processor?.firstName} ${processor?.lastName} (${processor?.email})` : "";
  const specialistName = (specialist) ? `${specialist?.firstName} ${specialist?.lastName} (${specialist?.email})` : "";
  const approvalChangedByName = (approvalChangedBy) ? `${approvalChangedBy?.firstName} ${approvalChangedBy?.lastName} (${approvalChangedBy?.email})` : "";

  const agencyName = agency?.name || "N/A";
  const agencyPurl = agency?.domain || "N/A";
  const emailRecipientList = emailRecipients.join("; ");
  const userDescription = user ? `${user.firstName} ${user.lastName} (${user.email})` : "Unknown";

  const loaAgentsText = (hasLOAAgents) ? "Yes" : "No";
  const unknownAgentText = (unknownAgent) ? "Yes" : "No";

  return {
    "GA Request ID": id,
    "Carrier Name": carrierName,
    "New Policy Unit": newPolicyUnit,
    "New Policy Amount": newPolicyAmount,
    "Renewal Unit": renewalUnit,
    "Renewal Amount": renewalAmount,
    "Has LOA Agents?": loaAgentsText,
    "Level": level,
    "Processor": processorName,
    "Carrier Status": carrierStatus,
    "Start Date": formatDate(startDate),
    "Agent NPN": npn,
    "Agent First Name": firstName,
    "Agent Last Name": lastName,
    "Agent Preferred Name": preferredName,
    "Agent Email": email,
    "Agent Phone": phone,
    "Agent Address": addressStreet,
    "Agent City": addressCity,
    "Agent State": addressState,
    "Agent ZIP": addressZip,
    "Agent Upline": upline,
    "Agent Top of Hierarchy": topOfHierarchy,
    "Agent Advisor": advisor,
    "Agent Commission Email": commissionEmail,
    "Agent Enrollment Email": enrollmentEmail,
    "GA Agent ID": ga,
    "Unknown Agent": unknownAgentText,
    "Notify Agent After Approval": notifyAfterApproval ? "Yes" : "No",
    "Notes": notes,
    "Agent or Agency": payee,
    "Overall Status": status,
    "Approval": approval,
    "Approval Changed By": approvalChangedByName,
    "Approval Changed At": formatTimestamp(approvalChangedAt),
    "Specialist (COB)": specialistName,
    "Agency": agencyName,
    "PURL": agencyPurl,
    "Submitted By": userDescription,
    "Email Recipients": emailRecipientList,
    "Created": formatTimestamp(createdAt),
    "Updated": formatTimestamp(updatedAt),
  };
}

// transformContractRequest takes a contract request and breaks it out into individual contracts
function transformGARequest(gaRequest, carriers, agencies, users) {
  return gaRequest.carriers.map((carrier) => transformCarrierData(gaRequest, carrier, carriers, agencies, users));
}

const headers = [
  "GA Request ID",
  "Carrier Name",
  "New Policy Unit",
  "New Policy Amount",
  "Renewal Unit",
  "Renewal Amount",
  "Has LOA Agents?",
  "Level",
  "Processor",
  "Carrier Status",
  "Start Date",
  "Agent NPN",
  "Agent First Name",
  "Agent Last Name",
  "Agent Preferred Name",
  "Agent Email",
  "Agent Phone",
  "Agent Address",
  "Agent City",
  "Agent State",
  "Agent ZIP",
  "Agent Upline",
  "Agent Top of Hierarchy",
  "Agent Advisor",
  "Agent Commission Email",
  "Agent Enrollment Email",
  "GA Agent ID",
  "Unknown Agent",
  "Notify Agent After Approval",
  "Notes",
  "Agent or Agency",
  "Overall Status",
  "Approval",
  "Approval Changed By",
  "Approval Changed At",
  "Specialist (COB)",
  "Agency",
  "PURL",
  "Submitted By",
  "Email Recipients",
  "Created",
  "Updated",
];

const exporter = async (records, fetchRelatedRecords) => {
  // Break out records into individual contracts
  const agencies = await fetchRelatedRecords(records, "agency", "agency");
  const carrierData = records.map((cr) => cr.carriers);
  const carriers = await fetchRelatedRecords(carrierData, "carrier", "carrier");

  // Fetch all user-related records (user, specialist, approvalChangedBy, and processor)
  const userIds = await fetchRelatedRecords(records, "user", "user");
  const specialists = await fetchRelatedRecords(records, "specialist", "user");
  const approvalChangedByUsers = await fetchRelatedRecords(records, "approvalChangedBy", "user");
  const processors = await fetchRelatedRecords(carrierData, "processor", "user");

  // Combine all user objects into a single map
  const users = { ...userIds, ...specialists, ...approvalChangedByUsers, ...processors };

  const data = records.map((cr) =>
    transformGARequest(cr, carriers, agencies, users)
  );
  const report = await jsonExport(data, { headers });
  return downloadCSV(report, "GA Requests");
};

export default exporter;
